import React, { useState } from 'react';
import { Badge } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { usersAPI } from '../../helpers/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  role: { id: string; name: string; displayName: string };
  createdAt: string;
}

interface Role {
  id: string;
  name: string;
  displayName: string;
}

interface UserDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  user: User | null;
  roles: Role[];
  onUpdate: () => void;
}

const getRoleBadgeColor = (roleName: string) => {
  switch (roleName) {
    case 'ADMIN':
      return 'danger';
    case 'MUHASEBE':
      return 'warning';
    default:
      return 'info';
  }
};

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  isOpen,
  toggle,
  user,
  roles,
  onUpdate,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  if (!user) return null;

  const handleToggleStatus = async () => {
    try {
      setTogglingStatus(true);
      await usersAPI.update(user.id, { isActive: !user.isActive });
      toast.success(user.isActive ? 'Kullanıcı pasif yapıldı' : 'Kullanıcı aktif yapıldı');
      onUpdate();
      toggle();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await usersAPI.delete(user.id);
      toast.success('Kullanıcı silindi');
      setShowDeleteConfirm(false);
      toggle();
      onUpdate();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme işlemi başarısız');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Lütfen her iki alanı da doldurun');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    try {
      setChangingPassword(true);
      await usersAPI.update(user.id, { password: newPassword });
      toast.success('Şifre başarıyla sıfırlandı');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Şifre sıfırlama başarısız');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Kullanıcı Detayı"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={
        <button
          type="button"
          className="side-modal-expand-btn"
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? 'Küçült' : 'Genişlet'}
        >
          <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
        </button>
      }
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div
          style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}
          className="rounded-3 p-3 mb-3"
        >
          <div className="d-flex align-items-center">
            <div
              className="avatar-lg rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
              style={{ width: 56, height: 56, fontSize: '1.25rem', flexShrink: 0 }}
            >
              {user.firstName[0]}{user.lastName[0]}
            </div>
            <div style={{ flex: 1 }}>
              <h4 className="mb-1" style={{ fontWeight: 600 }}>
                {user.firstName} {user.lastName}
              </h4>
              <div className="d-flex align-items-center gap-2">
                <Badge color={getRoleBadgeColor(user.role.name)}>
                  {user.role.displayName}
                </Badge>
                <Badge color={user.isActive ? 'success' : 'secondary'}>
                  {user.isActive ? 'Aktif' : 'Pasif'}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div
          style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }}
          className="rounded-3 p-3 mb-3"
        >
          <div
            className="text-muted small mb-3"
            style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}
          >
            Kullanıcı Bilgileri
          </div>
          <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef' }}
            >
              <span className="text-muted">E-posta</span>
              <span style={{ fontWeight: 500 }}>{user.email}</span>
            </div>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef' }}
            >
              <span className="text-muted">Rol</span>
              <Badge color={getRoleBadgeColor(user.role.name)}>
                {user.role.displayName}
              </Badge>
            </div>
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{ borderBottom: '1px solid #e9ecef' }}
            >
              <span className="text-muted">Durum</span>
              <Badge color={user.isActive ? 'success' : 'secondary'}>
                {user.isActive ? 'Aktif' : 'Pasif'}
              </Badge>
            </div>
            <div className="d-flex align-items-center justify-content-between px-3 py-2">
              <span className="text-muted">Kayıt Tarihi</span>
              <span style={{ fontWeight: 500 }}>
                {new Date(user.createdAt).toLocaleDateString('tr-TR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Password Reset Card */}
        <div
          style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #0ab39c' }}
          className="rounded-3 p-3 mb-3"
        >
          <div
            className="text-muted small mb-3"
            style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}
          >
            Şifre Sıfırla
          </div>
          <input
            type="password"
            className="form-control form-control-sm mb-2"
            placeholder="En az 6 karakter"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={6}
          />
          <input
            type="password"
            className="form-control form-control-sm mb-2"
            placeholder="Şifreyi tekrar girin"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            minLength={6}
          />
          <button
            type="button"
            className="btn btn-success btn-sm w-100"
            onClick={handleResetPassword}
            disabled={changingPassword}
          >
            <i className="ri-lock-password-line me-1"></i>
            {changingPassword ? 'Sıfırlanıyor...' : 'Şifreyi Sıfırla'}
          </button>
        </div>

        {/* Delete Action Card */}
        <div
          style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }}
          className="rounded-3 p-3 mb-3"
        >
          <div
            className="text-muted small mb-3"
            style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}
          >
            İşlemler
          </div>

          <button
            type="button"
            className={`btn btn-sm w-100 mb-2 ${user.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
            onClick={handleToggleStatus}
            disabled={togglingStatus}
          >
            <i className={`${user.isActive ? 'ri-close-circle-line' : 'ri-checkbox-circle-line'} me-1`}></i>
            {togglingStatus ? 'İşleniyor...' : (user.isActive ? 'Pasif Yap' : 'Aktif Yap')}
          </button>

          {!showDeleteConfirm ? (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm w-100"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <i className="ri-delete-bin-line me-1"></i>
              Kullanıcıyı Sil
            </button>
          ) : (
            <div className="rounded-3 p-3" style={{ background: '#fff4f2', border: '1px solid #f06548' }}>
              <p className="mb-3 text-center" style={{ fontSize: '0.9rem', color: '#f06548' }}>
                <i className="ri-error-warning-line me-1"></i>
                Emin misiniz? Bu işlem geri alınamaz.
              </p>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light flex-grow-1"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger flex-grow-1"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Siliniyor...' : 'Sil'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default UserDetailModal;
